import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowRight, Loader2, CheckCircle2, UploadCloud, X,
  IdCard, FileBadge, FileText, ShieldAlert, Wrench, Users, KeyRound,
  AlertTriangle, ClipboardList, MapPin, MessageCircle, Factory,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { validateRut, validateEmail, validateCelular, formatRut } from "@/lib/validation";
import { CONTACT_EMAIL, claimsWhatsappUrl, INDUSTRIA_OPTIONS } from "@/constants/site";
import { Honeypot, useAntiSpam } from "@/components/site/Honeypot";
import { usePartner } from "@/context/PartnerContext";
import { useReferral } from "@/context/ReferralContext";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIPOS = [
  { value: "Daños físicos al equipo", icon: Wrench },
  { value: "Daños a terceros", icon: Users },
  { value: "Robo del equipo", icon: KeyRound },
  { value: "Otro", icon: ShieldAlert },
];

const DOCS = [
  { key: "carnet", label: "Foto Carnet de identidad del contratante", icon: IdCard },
  { key: "documento_equipo", label: "Factura o documento del equipo asegurado", icon: FileText },
  { key: "foto_dano", label: "Foto del daño / equipo (opcional)", icon: FileBadge, optional: true },
];

const ROBO_TIPOS = ["Robo del equipo"];

const EMPTY = { nombre: "", rut: "", equipo: "", tipo_industria: "", email: "", celular: "" };
const RELATO_EMPTY = {
  fecha: "", hora: "", lugar: "", descripcion: "",
  terceros: "", lesionados: "", carabineros: "",
};

const Segmented = ({ label, value, onChange, options, testid }) => (
  <div className="space-y-1.5">
    <Label className="text-sm font-medium text-brand-ink">{label}</Label>
    <div className="flex gap-2" data-testid={testid}>
      {options.map((opt) => {
        const active = value === opt;
        return (
          <button
            type="button"
            key={opt}
            data-testid={`${testid}-${opt}`}
            onClick={() => onChange(active ? "" : opt)}
            className={`flex-1 rounded-xl border-2 px-3 py-2.5 text-sm font-medium transition-all ${
              active ? "border-brand-yellow bg-brand-yellow/10 text-brand-ink" : "border-gray-200 bg-white text-gray-600 hover:border-brand-yellow/50"
            }`}
          >
            {opt}
          </button>
        );
      })}
    </div>
  </div>
);

const FileDrop = ({ doc, file, onChange, error }) => {
  const Icon = doc.icon;
  return (
    <div>
      <Label className="text-sm font-medium text-brand-ink">
        {doc.label} {doc.optional && <span className="text-xs text-gray-400 ml-1">(opcional)</span>}
      </Label>
      <label
        data-testid={`drop-${doc.key}`}
        className={`mt-1.5 flex cursor-pointer items-center gap-3 rounded-xl border-2 border-dashed bg-white px-4 py-3 transition-colors ${
          error ? "border-red-500" : file ? "border-brand-yellow/60" : "border-gray-300 hover:border-brand-yellow/50"
        }`}
      >
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-brand-yellow/15 text-brand-ink">
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          {file ? (
            <p className="truncate text-sm font-medium text-brand-ink">{file.name}</p>
          ) : (
            <p className="text-sm text-gray-500"><span className="text-brand-ink font-medium">Toca para subir</span> · imagen o PDF</p>
          )}
        </div>
        {file ? (
          <button
            type="button"
            data-testid={`remove-${doc.key}`}
            onClick={(e) => { e.preventDefault(); onChange(null); }}
            className="flex h-7 w-7 items-center justify-center rounded-full text-gray-400 hover:bg-red-500 hover:text-white"
          >
            <X className="h-4 w-4" />
          </button>
        ) : (
          <UploadCloud className="h-5 w-5 text-gray-400" />
        )}
        <input
          type="file"
          accept="image/*,application/pdf"
          data-testid={`file-${doc.key}`}
          className="hidden"
          onChange={(e) => onChange(e.target.files?.[0] || null)}
        />
      </label>
      {error && <p data-testid={`error-${doc.key}`} className="mt-1 text-xs font-medium text-red-600">{error}</p>}
    </div>
  );
};

export default function ClaimForm() {
  const [tipos, setTipos] = useState([]);
  const [files, setFiles] = useState({ carnet: null, documento_equipo: null, foto_dano: null });
  const [form, setForm] = useState(EMPTY);
  const [relato, setRelato] = useState(RELATO_EMPTY);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const { honeypot, setHoneypot, getFields } = useAntiSpam();
  const { partner } = usePartner();
  const { ref } = useReferral();

  const update = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setErrors((e) => ({ ...e, [k]: undefined })); };
  const updateRelato = (k, v) => setRelato((r) => ({ ...r, [k]: v }));
  const setFile = (k, f) => { setFiles((prev) => ({ ...prev, [k]: f })); setErrors((e) => ({ ...e, [k]: undefined })); };
  const toggleTipo = (t) => {
    setErrors((e) => ({ ...e, tipos: undefined }));
    setTipos((prev) => (prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]));
  };

  const isRobo = tipos.some((t) => ROBO_TIPOS.includes(t));

  const validate = () => {
    const e = {};
    if (tipos.length === 0) e.tipos = "Selecciona al menos un tipo de siniestro";
    DOCS.filter((d) => !d.optional).forEach((d) => { if (!files[d.key]) e[d.key] = "Documento requerido"; });
    if (!form.nombre.trim()) e.nombre = "Ingresa tu nombre o razón social";
    if (!validateRut(form.rut)) e.rut = "RUT inválido";
    if (!form.equipo.trim()) e.equipo = "Describe el equipo (marca, modelo, serie)";
    if (!form.tipo_industria) e.tipo_industria = "Selecciona tipo de industria";
    if (!validateEmail(form.email)) e.email = "Email inválido";
    if (!validateCelular(form.celular)) e.celular = "Celular inválido";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) { toast.error("Revisa los campos marcados."); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("nombre", form.nombre);
      fd.append("rut", form.rut);
      fd.append("equipo", form.equipo);
      fd.append("tipo_industria", form.tipo_industria);
      fd.append("email", form.email);
      fd.append("celular", form.celular);
      tipos.forEach((t) => fd.append("tipos", t));
      const anti = getFields();
      fd.append("website", anti.website);
      fd.append("elapsed_ms", String(anti.elapsed_ms));
      if (partner) fd.append("partner", partner);
      if (ref) fd.append("ref", ref);
      Object.entries(relato).forEach(([k, v]) => { if (v) fd.append(k, v); });
      DOCS.forEach((d) => { if (files[d.key]) fd.append(d.key, files[d.key]); });
      await axios.post(`${API}/claims`, fd);
      setDone(true);
      toast.success("¡Siniestro recibido! Te contactaremos a la brevedad.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "No pudimos enviar tu solicitud. Intenta nuevamente.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "h-11 border-gray-300 bg-white focus-visible:ring-1 focus-visible:ring-brand-yellow focus-visible:border-brand-yellow";

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="border-b border-gray-200 bg-white">
        <div className="mx-auto max-w-5xl px-5 sm:px-8 h-16 flex items-center justify-between">
          <a href="/" className="flex items-center"><img src="/brand/logo.png" alt="MiMaquinaSegura.cl" className="h-9 w-auto" /></a>
          <a href="/" className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-brand-ink">
            <ArrowLeft className="h-4 w-4" /> Volver al sitio
          </a>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-5 sm:px-8 py-12 sm:py-16">
        <div className="text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-brand-yellow/40 bg-brand-yellow/10 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-brand-ink">
            <ShieldAlert className="h-3.5 w-3.5 text-brand-yellow" /> Asistencia siniestros
          </span>
          <h1 className="font-display mt-5 text-4xl sm:text-5xl font-bold tracking-tight text-brand-ink">Gestionemos tu siniestro</h1>
          <p className="mt-4 text-base sm:text-lg text-gray-600">
            Completa el formulario y adjunta tus documentos. Nuestro equipo te guiará en todo el proceso ante la aseguradora.
          </p>
          <div className="mt-6">
            <a
              href={claimsWhatsappUrl()}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="claim-whatsapp-btn"
              className="inline-flex h-12 items-center gap-2 rounded-full bg-brand-whatsapp px-6 font-semibold text-white shadow-lg shadow-brand-whatsapp/30 transition-all hover:-translate-y-0.5"
            >
              <MessageCircle className="h-5 w-5" /> Asistencia inmediata por WhatsApp
            </a>
          </div>
        </div>

        {done ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
            data-testid="claim-success"
            className="mt-10 rounded-3xl border border-gray-200 bg-white p-10 text-center shadow-xl shadow-brand-ink/5"
          >
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-brand-yellow/15 text-brand-ink">
              <CheckCircle2 className="h-8 w-8" />
            </div>
            <h3 className="font-display mt-6 text-2xl font-bold text-brand-ink">¡Recibimos tu siniestro!</h3>
            <p className="mt-3 text-gray-600">Te contactaremos a la brevedad para acompañarte en el proceso. Cualquier duda: {CONTACT_EMAIL}</p>
            <Button onClick={() => window.location.assign("/")} className="mt-6 rounded-full bg-brand-ink hover:bg-brand-inkHover text-white">Volver al inicio</Button>
          </motion.div>
        ) : (
          <form onSubmit={handleSubmit} noValidate data-testid="claim-form" className="mt-10 rounded-3xl border border-gray-200 bg-[#FAFAFA] p-6 sm:p-9 shadow-xl shadow-brand-ink/5">
            <Honeypot value={honeypot} onChange={setHoneypot} />
            {/* Tipo */}
            <div className="flex items-center gap-2 text-brand-ink">
              <ShieldAlert className="h-5 w-5 text-brand-yellow" />
              <h2 className="font-display text-lg font-semibold">1. Tipo de siniestro</h2>
            </div>
            <p className="mt-1 text-sm text-gray-500">Puedes seleccionar más de una opción.</p>
            <div className="mt-4 grid sm:grid-cols-2 gap-3">
              {TIPOS.map((t) => {
                const active = tipos.includes(t.value);
                const Icon = t.icon;
                return (
                  <button
                    type="button" key={t.value}
                    data-testid={`tipo-${t.value}`}
                    onClick={() => toggleTipo(t.value)}
                    className={`flex items-center gap-3 rounded-xl border-2 px-4 py-3 text-left transition-all ${
                      active ? "border-brand-yellow bg-brand-yellow/10" : "border-gray-200 bg-white hover:border-brand-yellow/40"
                    }`}
                  >
                    <div className={`flex h-9 w-9 items-center justify-center rounded-lg ${active ? "bg-brand-yellow text-brand-ink" : "bg-gray-100 text-gray-500"}`}>
                      <Icon className="h-4 w-4" />
                    </div>
                    <span className={`text-sm font-medium ${active ? "text-brand-ink" : "text-gray-600"}`}>{t.value}</span>
                    {active && <CheckCircle2 className="ml-auto h-5 w-5 text-brand-yellow" />}
                  </button>
                );
              })}
            </div>
            {errors.tipos && <p data-testid="error-tipos" className="mt-2 text-xs font-medium text-red-600">{errors.tipos}</p>}

            {isRobo && (
              <motion.div
                initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }}
                data-testid="robo-warning"
                className="mt-4 flex items-start gap-3 rounded-xl border-2 border-red-500/40 bg-red-500/5 p-4"
              >
                <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-red-600" />
                <div>
                  <p className="text-sm font-bold text-red-600">Importante — en caso de robo</p>
                  <p className="mt-1 text-sm text-brand-ink">
                    Debes hacer la denuncia de manera <strong>inmediata</strong> en la comisaría de Carabineros más cercana. Sin la constancia/denuncia, la aseguradora no podrá gestionar tu siniestro.
                  </p>
                </div>
              </motion.div>
            )}

            {/* Documentos */}
            <div className="mt-8 flex items-center gap-2 text-brand-ink">
              <UploadCloud className="h-5 w-5 text-brand-yellow" />
              <h2 className="font-display text-lg font-semibold">2. Documentos</h2>
            </div>
            <div className="mt-4 space-y-3">
              {DOCS.map((d) => (
                <FileDrop key={d.key} doc={d} file={files[d.key]} onChange={(f) => setFile(d.key, f)} error={errors[d.key]} />
              ))}
            </div>

            {/* Contacto */}
            <div className="mt-8 flex items-center gap-2 text-brand-ink">
              <IdCard className="h-5 w-5 text-brand-yellow" />
              <h2 className="font-display text-lg font-semibold">3. Tus datos</h2>
            </div>
            <div className="mt-4 grid sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2 space-y-1.5">
                <Label htmlFor="nombre" className="text-sm font-medium text-brand-ink">Nombre o razón social</Label>
                <Input id="nombre" data-testid="claim-nombre-input" value={form.nombre} onChange={(e) => update("nombre", e.target.value)} placeholder="Juan Pérez / Constructora Los Andes SpA" className={inputCls} />
                {errors.nombre && <p data-testid="error-nombre" className="text-xs font-medium text-red-600">{errors.nombre}</p>}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="rut" className="text-sm font-medium text-brand-ink">RUT</Label>
                <Input id="rut" data-testid="claim-rut-input" value={form.rut} onChange={(e) => update("rut", e.target.value)} onBlur={(e) => e.target.value && update("rut", formatRut(e.target.value))} placeholder="12.345.678-5" className={inputCls} />
                {errors.rut && <p data-testid="error-rut" className="text-xs font-medium text-red-600">{errors.rut}</p>}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="tipo_industria" className="text-sm font-medium text-brand-ink">Tipo de industria</Label>
                <Select value={form.tipo_industria} onValueChange={(v) => update("tipo_industria", v)}>
                  <SelectTrigger id="tipo_industria" data-testid="claim-industria-select" className={inputCls}>
                    <SelectValue placeholder="Selecciona" />
                  </SelectTrigger>
                  <SelectContent>
                    {INDUSTRIA_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.tipo_industria && <p data-testid="error-tipo_industria" className="text-xs font-medium text-red-600">{errors.tipo_industria}</p>}
              </div>
              <div className="sm:col-span-2 space-y-1.5">
                <Label htmlFor="equipo" className="text-sm font-medium text-brand-ink">Equipo afectado (marca, modelo, serie o descripción)</Label>
                <Input id="equipo" data-testid="claim-equipo-input" value={form.equipo} onChange={(e) => update("equipo", e.target.value)} placeholder="Tractor John Deere 6110, serie ABC123..." className={inputCls} />
                {errors.equipo && <p data-testid="error-equipo" className="text-xs font-medium text-red-600">{errors.equipo}</p>}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="celular" className="text-sm font-medium text-brand-ink">Celular</Label>
                <Input id="celular" data-testid="claim-celular-input" value={form.celular} onChange={(e) => update("celular", e.target.value)} placeholder="+56 9 1234 5678" className={inputCls} />
                {errors.celular && <p data-testid="error-celular" className="text-xs font-medium text-red-600">{errors.celular}</p>}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="email" className="text-sm font-medium text-brand-ink">Email</Label>
                <Input id="email" data-testid="claim-email-input" type="email" value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="tucorreo@email.com" className={inputCls} />
                {errors.email && <p data-testid="error-email" className="text-xs font-medium text-red-600">{errors.email}</p>}
              </div>
            </div>

            {/* Relata */}
            <div className="mt-8 flex items-center gap-2 text-brand-ink">
              <ClipboardList className="h-5 w-5 text-brand-yellow" />
              <h2 className="font-display text-lg font-semibold">4. Relata los hechos</h2>
            </div>
            <p className="mt-1 text-sm text-gray-500">Cuéntanos cómo ocurrió. Mientras más detalles, mejor gestionaremos tu caso.</p>
            <div className="mt-4 grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="fecha" className="text-sm font-medium text-brand-ink">Fecha del siniestro</Label>
                <Input id="fecha" type="date" data-testid="claim-fecha-input" value={relato.fecha} onChange={(e) => updateRelato("fecha", e.target.value)} className={inputCls} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="hora" className="text-sm font-medium text-brand-ink">Hora aproximada</Label>
                <Input id="hora" type="time" data-testid="claim-hora-input" value={relato.hora} onChange={(e) => updateRelato("hora", e.target.value)} className={inputCls} />
              </div>
              <div className="sm:col-span-2 space-y-1.5">
                <Label htmlFor="lugar" className="text-sm font-medium text-brand-ink">Lugar (dirección, faena, comuna)</Label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input id="lugar" data-testid="claim-lugar-input" value={relato.lugar} onChange={(e) => updateRelato("lugar", e.target.value)} placeholder="Fundo El Roble, Ruta H-30, Rancagua" className={`${inputCls} pl-9`} />
                </div>
              </div>
              <div className="sm:col-span-2 space-y-1.5">
                <Label htmlFor="descripcion" className="text-sm font-medium text-brand-ink">Relato del siniestro</Label>
                <Textarea id="descripcion" data-testid="claim-descripcion-input" value={relato.descripcion} onChange={(e) => updateRelato("descripcion", e.target.value)} placeholder="Describe con tus palabras qué pasó..." rows={4} className="border-gray-300 bg-white focus-visible:ring-1 focus-visible:ring-brand-yellow focus-visible:border-brand-yellow" />
              </div>
              <Segmented label="¿Hubo terceros involucrados?" value={relato.terceros} onChange={(v) => updateRelato("terceros", v)} options={["Sí", "No"]} testid="claim-terceros" />
              <Segmented label="¿Hubo lesionados?" value={relato.lesionados} onChange={(v) => updateRelato("lesionados", v)} options={["Sí", "No"]} testid="claim-lesionados" />
              <Segmented label="¿Llegaron Carabineros?" value={relato.carabineros} onChange={(v) => updateRelato("carabineros", v)} options={["Sí", "No"]} testid="claim-carabineros" />
            </div>

            <Button type="submit" data-testid="claim-submit-button" disabled={loading} className="mt-8 w-full rounded-full bg-brand-ink hover:bg-brand-inkHover text-white text-base font-semibold py-3.5 transition-all hover:-translate-y-0.5 shadow-lg shadow-brand-ink/20">
              {loading ? <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Enviando...</> : <>Enviar siniestro <ArrowRight className="ml-1 h-4 w-4" /></>}
            </Button>
            <p className="mt-3 text-center text-xs text-gray-500">Tus documentos se almacenan de forma segura y privada.</p>
          </form>
        )}
      </main>
    </div>
  );
}
